import { CaretDownIcon } from "@phosphor-icons/react";
import { buttonVariants } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/data-display/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export interface UserDropdownTriggerProps {
  /** Avatar initial (e.g. first letter of email) */
  initial: string;
  /** Tenant name; when provided, shown with avatar + chevron on sm+, hidden on smaller screens */
  tenantName?: string;
}

/**
 * Default trigger for {@link UserDropdown}: avatar + tenant name + chevron on sm+; avatar-only on smaller screens.
 * Rounded-full on hover only when it looks like avatar-only (small screen with tenantName, or no tenantName).
 */
const UserDropdownTrigger = ({
  initial,
  tenantName,
}: UserDropdownTriggerProps) =>
  tenantName !== undefined && tenantName !== "" ? (
    <button
      type="button"
      aria-label="Open user menu"
      className={cn(
        "flex items-center p-2 transition-colors sm:gap-2 sm:px-2 sm:py-1 sm:rounded-md",
        "hover:bg-slate-100 rounded-full",
      )}
    >
      <Avatar className="h-5 w-5 shrink-0">
        <AvatarFallback>{initial}</AvatarFallback>
      </Avatar>
      <span className="hidden text-xs text-slate-900 sm:inline capitalize">
        {tenantName}
      </span>
      <CaretDownIcon
        weight="fill"
        className="hidden size-4 shrink-0 text-slate-900 sm:block"
      />
    </button>
  ) : (
    // Native button: react-aria's Button stops pointerdown propagation
    // (react-aria-components >= 1.20), which keeps the Radix trigger from opening.
    <button
      type="button"
      aria-label="Open user menu"
      className={cn(
        buttonVariants({ variant: "ghost", size: "icon" }),
        "hover:rounded-full focus-visible:ring-2 focus-visible:ring-blue-750 focus-visible:ring-offset-2",
      )}
    >
      <Avatar className="h-5 w-5">
        <AvatarFallback>{initial}</AvatarFallback>
      </Avatar>
    </button>
  );

export interface UserDropdownProps {
  /** User email displayed in the dropdown header and used for avatar initial */
  email: string;
  /** Called when the user selects "Log out" */
  onLogout?: () => void;
  /** Tenant/organization name shown next to avatar on larger screens; hidden on small screens (avatar-only). */
  tenantName?: string;
  /** Applied to the trigger. */
  className?: string;
  /** When true, the dropdown is open by default (e.g. for Storybook). */
  defaultOpen?: boolean;
}

/**
 * User account dropdown with optional custom trigger, email, and log out action.
 * Default trigger: avatar + tenant name + chevron on sm+; avatar-only on smaller screens.
 */
export const UserDropdown = ({
  email,
  onLogout,
  tenantName,
  className,
  defaultOpen,
}: UserDropdownProps) => {
  const initial = email.charAt(0).toUpperCase() || "?";

  return (
    <DropdownMenu defaultOpen={defaultOpen}>
      <DropdownMenuTrigger asChild>
        <span
          className={cn(
            "inline-flex cursor-pointer rounded-full sm:rounded-md border border-transparent transition-colors",
            "data-[state=open]:cursor-default data-[state=open]:border-violet-700 data-[state=open]:bg-[#4646460A]",
            className,
          )}
        >
          <UserDropdownTrigger initial={initial} tenantName={tenantName} />
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="min-w-[205px] rounded-[12px] p-3 pt-4 shadow-[0_0_12px_0_rgba(0,0,0,0.15)]"
      >
        <div className="flex items-center gap-2 text-xs text-gray-700">
          <Avatar className="h-6 w-6 shrink-0">
            <AvatarFallback>{initial}</AvatarFallback>
          </Avatar>
          <span className="whitespace-nowrap">{email}</span>
        </div>
        <DropdownMenuSeparator className="mx-0 my-2.5" />
        <DropdownMenuItem
          onSelect={onLogout}
          className="rounded-md px-3 py-2 text-xs"
        >
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
